/* eslint-disable camelcase */
import { FunctionComponent, useContext, useEffect, useState } from 'react'
import PlacesAutocomplete, { geocodeByAddress } from 'react-places-autocomplete'
import styled, { ThemeContext } from 'styled-components'
import { v4 as uuid } from 'uuid'

import ErrorBox from 'components/ErrorBox'
import RoundedButton from 'components/RoundedButton'
import Spacer from 'components/Spacer'

import { useUser } from 'services/swr/useUser'

import formatLocation from 'utils/formatLocation'
import getSellHomeriseUrl from 'utils/getSellHomeriseUrl'
import validateStreet from 'utils/inputValidation/streetAddress'
import { isChrome } from 'utils/userAgent'

import * as T from 'types'
import * as E from 'types/enums'

const NO_GOOGLE_MATCH
  = 'Please retype the address and select one of the Google Maps suggestions. If none of the options match your address or there are no suggestions, please'

const GOOGLE_MAPS_ERROR = 'Google Maps does not recognize this address. Please try again or'
const LOOKUP_ERROR = "We couldn't look up on your address right now. Please try again or"
const NO_ZIP_ERROR = 'No zipcode found. Please reselect a valid address or'

const NO_STREET_ERROR
  = 'Street address does not include a street number. Please reselect a valid address or'

const placesAutocompleteSearchOptions = {
  componentRestrictions: { country: 'us' },
  types: ['address'],
}

interface IDropShadowProp {
  dropShadow?: string
}
interface IWidthProps {
  width?: string
  tabletWidth?: string
  desktopWidth?: string
  flexDirectionTablet?: string
  flexDirectionDesktop?: string
}
const AutocompleteContainer = styled.div<IWidthProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  ${props => props.width && `width: ${props.width};`}
  z-index: ${props => props.theme.zIndices.level5};

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    flex-direction: row;
    ${props => props.tabletWidth && `width: ${props.tabletWidth};`}
    ${props => props.flexDirectionTablet && `flex-direction: ${props.flexDirectionTablet};`}
  }
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    flex-direction: row;
    ${props => props.desktopWidth && `width: ${props.desktopWidth};`}
    ${props => props.flexDirectionDesktop && `flex-direction: ${props.flexDirectionDesktop};`}
  }
`
interface IErrorMessageProps {
  errorTopPosition?: string
}
const ErrorsContainer = styled.div<IErrorMessageProps>`
  position: absolute;
  width: 100%;
  top: 130px;
  left: 0;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    top: 58px;
    ${props => props.errorTopPosition && `top: ${props.errorTopPosition};`}
  }
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    top: 58px;
  }
`

const AddressInputContainer = styled.div`
  display: flex;
  width: 100%;
`

interface IButtonWidthProps {
  width?: number
  buttonDesktopWidth?: number
}
interface IZIndexProp {
  zIndex?: number
}
const ButtonContainer = styled.div<IButtonWidthProps & IZIndexProp & IDropShadowProp>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  ${props =>
    props.dropShadow
    && `
    filter: drop-shadow(${props.dropShadow});
  `}
  @media(min-width: ${props => props.theme.metrics.tablet}px) {
    ${props => props.width && `width: ${props.width}px;`}

    // cannot apply to mobile - will cause button to appear above suggestions
    ${props => props.zIndex && `z-index: ${props.zIndex};`}
  }
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    ${props => props.buttonDesktopWidth && `width: ${props.buttonDesktopWidth}px;`}
  }
`

interface ICenterProp {
  center?: boolean
}
const LoggedInStateButtonContainer = styled.div<IZIndexProp & ICenterProp>`
  display: flex;
  justify-content: center;
  width: 100%;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    justify-content: ${props => (props.center ? 'center' : 'flex-start')};
  }
`

interface IErrorProps {
  error: boolean
}
interface IInvalidProps {
  invalid?: boolean
}
interface IDropdownOpenProps {
  dropdownOpen?: boolean
}
interface IBackgroundProps {
  background?: string
}
const Input = styled.input.attrs({ type: 'text' })<
  IBackgroundProps & IDropdownOpenProps & IErrorProps & IInvalidProps
>`
  padding-top: 1px; /* font correction */
  padding-left: 20px;
  background: ${props => props.background || props.theme.colors.grey16};
  ${props => props.error && `background: ${props.theme.colors.transparentRedAlpha10};`}
  border: ${props => `
    ${props.error ? 2 : 1}px solid ${props.theme.colors[props.error ? 'red' : 'neutral100']}
  `};
  box-shadow: 0px 4px 8px ${props => props.theme.colors.transparentGrey23Alpha16};
  border-radius: 8px;
  width: 100%;
  height: 52px;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;

  caret-color: ${props => props.theme.colors.blue500};
  color: ${props => props.theme.colors.grey27};

  &::placeholder {
    color: ${props => props.theme.colors.neutral300};
    opacity: 1;
    font-weight: 400;
  }
  &::-webkit-input-placeholder {
    color: ${props => props.theme.colors[props.error ? 'red500' : 'neutral300']};
    opacity: 1;
    font-weight: 400;
  }

  &:focus {
    outline: none;
    border: 2px solid ${props => props.theme.colors[props.invalid ? 'red500' : 'blue500']};
  }
`

const PlaceInputContainer = styled.div<IDropShadowProp>`
  position: relative;
  flex-grow: 1;
  z-index: 3;
  ${props =>
    props.dropShadow
    && `
     filter: drop-shadow(${props.dropShadow});
  `}
`

const PlaceInputBackingContainer = styled.div`
  position: absolute;
  background: ${props => props.theme.colors.white};
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: -1;
  border-radius: 10px;
`

interface IActiveProp {
  active?: boolean
}
const DropdownContainer = styled.div<IActiveProp>`
  display: none;
  position: absolute;
  top: 48px;
  left: 0;
  background-color: ${props => props.theme.colors.white};
  border: none;
  width: 100%;
  z-index: 100;
  margin: 10px 0px;
  text-align: left;
  letter-spacing: 1px;
  font-size: 16px;
  ${props =>
    props.active && `box-shadow: 2px 2px 10px 2px ${props.theme.colors.transparentBlackAlpha10};`}

  ${Input}:focus + & {
    display: block;
  }
`

const AutocompleteOption = styled.div`
  padding: 10px 10px 0px;
  font-style: normal;
  font-weight: 900;
  font-size: 11px;
  line-height: 15px;
  letter-spacing: normal;
  color: ${props => props.theme.colors.black};

  &:last-child {
    padding-bottom: 10px;
  }

  cursor: pointer;
`

const initialAddressData: T.ICreateListingData = {
  aptNum: '',
  city: '',
  country: '',
  county: '',
  latitude: 0,
  longitude: 0,
  state: '',
  street: '',
  zip: '',
}

interface IProps {
  width?: string
  tabletWidth?: string
  desktopWidth?: string
  inputBackground?: string
  buttonBackground?: string
  buttonBackgroundHover?: string
  buttonFontSize?: number
  buttonTextColor?: string
  buttonWidth?: number
  buttonDesktopWidth?: number
  dropShadow?: string
  addressInputRef?: React.RefObject<HTMLInputElement>
  flexDirectionTablet?: string
  flexDirectionDesktop?: string
  errorTopPosition?: string
  center?: boolean
}

const AddressAutocomplete: FunctionComponent<IProps> = ({
  width,
  tabletWidth,
  desktopWidth,
  inputBackground,
  buttonBackground,
  buttonBackgroundHover,
  buttonFontSize,
  buttonTextColor,
  buttonWidth,
  buttonDesktopWidth,
  center,
  dropShadow,
  addressInputRef,
  flexDirectionTablet,
  flexDirectionDesktop,
  errorTopPosition,
}) => {
  const theme = useContext(ThemeContext)

  // user (to check if logged in)
  const { user, isLoading: loadingUser } = useUser()

  // autocomplete
  const [autoComplete, setAutoComplete] = useState('off')
  useEffect(() => {
    const mode = isChrome() ? 'nope' : 'off'
    setAutoComplete(mode)
  }, [])

  // error state
  const [error, setError] = useState('')

  const resetErrors = () => {
    if (error) setError('')
  }

  const [submitted, setSubmitted] = useState(false)

  const [address, setAddress] = useState('')
  const [addressParts, setAddressParts] = useState<T.ICreateListingData>(initialAddressData)
  const [addressSelected, setAddressSelected] = useState(false)

  useEffect(() => {
    if (error) {
      setAddress('')
      setAddressSelected(false)
    }
  }, [error])

  const updateAddressParts = async (geocoderResult: google.maps.GeocoderResult) => {
    if (!geocoderResult) return setError(GOOGLE_MAPS_ERROR)

    const locationData = await formatLocation(geocoderResult)
    if (!locationData.zip) return setError(NO_ZIP_ERROR)

    if (!validateStreet(locationData.street || '')) return setError(NO_STREET_ERROR)

    setAddressParts({ ...addressParts, ...locationData })
  }

  const handleAddressSelect = async (selectedAddress: string) => {
    try {
      const results = await geocodeByAddress(selectedAddress)

      if (results && !!results[0]) {
        updateAddressParts(results[0])
        setAddress(results[0]?.formatted_address)
        setAddressSelected(true)
      }
    } catch (err) {
      setError(LOOKUP_ERROR)
    }
  }

  const handleAddressChange = (value: string) => {
    setAddress(value)
    setAddressSelected(false)
    resetErrors()
  }

  const handleSubmit = () => {
    if (!addressSelected) return setError(NO_GOOGLE_MATCH)

    setSubmitted(true)
    window.location.assign(getSellHomeriseUrl(address))
  }

  if (loadingUser) return null

  return (
    <AutocompleteContainer
      width={width}
      tabletWidth={tabletWidth}
      desktopWidth={desktopWidth}
      flexDirectionTablet={flexDirectionTablet}
      flexDirectionDesktop={flexDirectionDesktop}
    >
      {!user && (
        <>
          <AddressInputContainer>
            <PlacesAutocomplete
              value={address}
              onChange={handleAddressChange}
              onSelect={handleAddressSelect}
              searchOptions={placesAutocompleteSearchOptions}
              shouldFetchSuggestions={address.length > 2}
              // googleCallbackName="mapsCallback"
            >
              {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                <PlaceInputContainer dropShadow={dropShadow}>
                  {/*
                  // @ts-ignore */}
                  <Input
                    {...getInputProps({
                      className: 'street-address',
                    })}
                    data-testid="enterYourAddress"
                    ref={addressInputRef}
                    dropdownOpen={!!suggestions?.length || loading}
                    error={!!error && !addressSelected}
                    name={uuid()} // force browser not to suggest previous inputs
                    placeholder="Enter Your Address"
                    autoComplete={autoComplete}
                    background={inputBackground}
                    disabled={submitted}
                  />
                  <DropdownContainer active={suggestions.length > 0}>
                    {loading && null}
                    {suggestions.map(suggestion => (
                      <AutocompleteOption
                        {...getSuggestionItemProps(suggestion)}
                        // not including key causes a warning, but adding it to the component
                        // causes the options not to display + selection to stop working locally
                        // key={suggestion.placeId}
                      >
                        {suggestion.description}
                      </AutocompleteOption>
                    ))}
                  </DropdownContainer>
                  <PlaceInputBackingContainer />
                </PlaceInputContainer>
              )}
            </PlacesAutocomplete>
          </AddressInputContainer>
          <Spacer
            size={12}
            desktopSize={24}
            desktopLargeSize={24}
          />
          <ButtonContainer
            width={buttonWidth}
            zIndex={theme.zIndices.level2}
            dropShadow={dropShadow}
            buttonDesktopWidth={buttonDesktopWidth}
          >
            <RoundedButton
              onClick={handleSubmit}
              type={E.ButtonType.submit}
              text="See Your Savings"
              background={buttonBackground || theme.colors.blue500}
              hoverBackground={buttonBackgroundHover || theme.colors.blue9}
              color={buttonTextColor || theme.colors.white}
              fontSize={buttonFontSize || 18}
              boxShadow={`0px 12px 24px ${theme.colors.transparentGrey23Alpha16}`}
              lineHeight={16}
              radius={10}
              height={52}
              letterSpacing="1px"
              fullWidth
            />
          </ButtonContainer>
        </>
      )}
      {user && (
        <LoggedInStateButtonContainer center={center}>
          <RoundedButton
            href="/listing/create"
            text="See Your Savings"
            background={buttonBackground || theme.colors.blue500}
            hoverBackground={buttonBackgroundHover || theme.colors.blue9}
            color={buttonTextColor || theme.colors.white}
            fontSize={buttonFontSize || 18}
            boxShadow={`0px 12px 24px ${theme.colors.transparentGrey23Alpha16}`}
            lineHeight={16}
            radius={50}
            height={50}
            width={300}
            letterSpacing="1px"
          />
        </LoggedInStateButtonContainer>
      )}
      <ErrorsContainer errorTopPosition={errorTopPosition}>
        {!!error && (
          <ErrorBox
            error={error}
            showChatMessage={!addressParts.listing || error === NO_GOOGLE_MATCH}
            chatMessage="chat with us for help."
            prepopulatedIntercomMessage="None of the Google Maps suggestions match my address/there are no suggestions."
          />
        )}
      </ErrorsContainer>
    </AutocompleteContainer>
  )
}

export default AddressAutocomplete
